// shared-lib/group-customization/refs.js
//
// Typed endpoint refs. A link endpoint names anything with a pose: an
// object, an appliance's fixture group, a bay of a run, a run end, an
// opening, a wall. A bare string is the legacy form and means
// `{ kind: 'object', id }`, so every link written before refs existed keeps
// loading unchanged.
//
// Walls and openings are FIXED nodes — their pose is known and a solve never
// moves them. Everything else is free unless the user is dragging it.
//
// See reference-docs/layout-relationships/overview.md §2.2.

const REF_KINDS = new Set([
  'object',
  'appliance',
  'bay',
  'opening',
  'wall',
  'run-end',
]);
const FIXED_KINDS = new Set(['wall', 'opening']);

/**
 * @param {string|Object|null} x — a bare object id or a typed ref
 * @returns {Object|null} `{ kind, … }`, or null for a null/undefined input
 * @throws on an unknown `kind` — a typo must fail loudly, never become an
 *   endpoint nothing ever matches.
 */
function normalizeRef(x) {
  if (x == null) return null;
  if (typeof x === 'string') return { kind: 'object', id: x };
  if (typeof x !== 'object') throw new Error(`unknown ref: ${String(x)}`);
  const kind = x.kind || 'object';
  if (!REF_KINDS.has(kind)) throw new Error(`unknown ref kind: ${kind}`);
  return { ...x, kind };
}

/**
 * Stable string identity for a ref. Two refs that name the same thing key
 * the same; defaults (`edge`, `at`) are spelled out so a bare
 * `{kind:'opening', id}` and an explicit `edge:'center'` agree.
 */
function refKey(x) {
  const r = normalizeRef(x);
  if (!r) return null;
  switch (r.kind) {
    case 'object':
      return `object:${r.id}`;
    case 'appliance':
      return `appliance:${r.fixtureGroupId}`;
    case 'bay':
      // `bayId` is the stable identity (#1526) — `anchorParam` mutates under
      // the ref (the center-on apply's own write-back rewrites it) and
      // `runId` gains aliases on corner joins. The param form remains the
      // key for legacy refs written before bays carried ids.
      return r.bayId != null
        ? `bay:${r.bayId}`
        : `bay:${r.runId}:${Number(r.anchorParam).toFixed(4)}`;
    case 'opening':
      return `opening:${r.id}:${r.edge || 'center'}`;
    case 'wall':
      return `wall:${r.id}:${r.at || 'center'}`;
    case 'run-end':
      return `run-end:${r.runId}:${r.end}`;
    default:
      throw new Error(`unknown ref kind: ${r.kind}`);
  }
}

/** True for a ref whose pose is fixed geometry (wall / opening). */
function isFixedRef(x) {
  const r = normalizeRef(x);
  return !!r && FIXED_KINDS.has(r.kind);
}

module.exports = { REF_KINDS, FIXED_KINDS, normalizeRef, refKey, isFixedRef };
